import { Pagination } from 'api/pagination';
import { SearchAnimal } from 'api/query/search';
import searchApi from 'api/query/search/search-api';
import { SearchParams } from 'api/query/search/search-params';
import { SearchResponse } from 'api/query/search/search-response';
import { SearchSort } from 'api/query/search/search-sort';
import { FilterValue } from 'api/query/shared/filter-value';
import Axios, { CancelTokenSource } from 'axios';
import { AnimalCardSimple } from 'components/animal-card/animal-card-simple';
import { getTags, showTag } from 'components/animal-tags/animal-tags-utils';
import { SearchError } from 'components/search-error/search-error';
import TableFade from 'components/table/table-fade';
import { environment } from 'environment';
import { FilterCategory } from 'pages/search/filters/filter-category.enum';
import { FiltersStep } from 'pages/search/filters/filters-step.enum';
import { useFilters } from 'pages/search/filters/use-filters';
import { usePrime } from 'pages/search/prime/use-prime';
import { ResultsNumConstants } from 'pages/search/results/results-view/results-num-constants.enum';
import { SortDir } from 'pages/search/results/sort-dir.enum';
import { useSearch } from 'pages/search/use-search';
import { IndividualFilter } from 'pages/shared/individual-filter.enum';
import { useResults } from 'pages/shared/use-results';
import React, { useCallback, useEffect, useState } from 'react';
import { ASBVSnapshot } from '../asbv-snapshot/asbv-snapshot';
import { ChartAnimal } from '../chart-animal';
import { CompareAnimalsProps } from './compare-animals-props';
import './compare-animals.scss';

const dataInitialState = {
  results: [],
  analysisDate: '',
  sortBy: '',
  sortDir: SortDir.BestToWorst,
  totalCount: 0,
};

/**
 * The root component for the animal details page.
 * @param props
 */

export const CompareAnimals: React.FC<CompareAnimalsProps> = (props) => {
  const {
    animals,
    db,
    subIndex,
  } = props;

  const {
    sortByASBV,
    customASBVs,
  } = useResults();

  const {
    currentPrime, currentFilters, setDataLoading, setCurrentFilters, setFiltersStep, setCurrentPrime,
  } = useSearch();

  const { filters } = useFilters();

  const {
    db: primeDb, userType, traits,
  } = currentPrime;

  const { primeFilters } = usePrime();

  const [loading, setLoading] = useState<{ initial: boolean, more: boolean }>({ initial: false, more: false });
  const [data, setData] = useState<SearchResponse>(dataInitialState);
  const [showRetry, setShowRetry] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [p, setP] = useState<Pagination>({ pageNumber: 1, pageSize: ResultsNumConstants.PageSize });
  const [chartAnimals, setChartAnimals] = useState<ChartAnimal[]>([]);

  const [animalsIds] = useState<string[]>(animals?.split('+', 5));

  const showChart = environment.featureFlags.comparePageChart;

  const onStart = useCallback((params: SearchParams, paging: Pagination, sortOption: SearchSort) => {
    setHasError(false);
    setLoading({ initial: paging.pageNumber === 1, more: paging.pageNumber !== 1 });
    setDataLoading(true);
  }, [setDataLoading]);

  const onEnd = useCallback((response: SearchResponse, paging: Pagination, sortOption: SearchSort) => {
    if (paging.pageNumber === 1) {
      setData(response);
    } else {
      setData((prev) => ({
        ...prev,
        results: prev.results.concat(response.results),
      }));
    }

    setTimeout(() => {
      setLoading({ initial: false, more: false });
      setDataLoading(false);
    }, ResultsNumConstants.Delay);
  }, [setDataLoading]);

  const onError = useCallback((reset?: boolean) => {
    if (reset) {
      setData(dataInitialState);
    }
    setHasError(true);
    setLoading({ initial: false, more: false });
    setDataLoading(false);
    setShowRetry(true);
  }, [setDataLoading]);

  const getSearchAnimals = useCallback(async (page: Pagination, sortOption: SearchSort, source: CancelTokenSource) => {
    setShowRetry(false);

    const searchFilters = filters;
    searchFilters.individual = new Map<IndividualFilter, FilterValue>([
      [IndividualFilter.AnimalId, { value: animalsIds.toString() }],
    ]);

    const params: SearchParams = {
      category: FilterCategory.Individual,
      filters: currentFilters,
      db: primeDb.id,
      userType: userType.id,
      subIndex: traits.id,
      customASBVs,
    };
    try {
      onStart(params, page, sortOption);
      const results = await searchApi.get(params, page, sortOption, source.token);
      onEnd(results, page, sortOption);
    } catch (e) {
      // Only reset results if an initial/updated search
      onError(data.totalCount !== animalsIds.length);
    }
  }, [animalsIds, currentFilters, customASBVs, data.totalCount, filters, onEnd, onError, onStart, primeDb.id, traits.id, userType.id]);

  useEffect(() => {
    if (showChart) {
      setChartAnimals(data.results.map((animal: SearchAnimal): ChartAnimal => ({
        id: animal.animalId,
        asbvs: animal.asbvs,
      })));
    }
  }, [data.results, showChart]);

  /**
   * Loads the data when the component mounts.
   */
  useEffect(() => {
    const source = Axios.CancelToken.source();

    setCurrentPrime(primeFilters);
    setCurrentFilters(filters);
    setFiltersStep(FiltersStep.Results);

    const paging = {
      pageNumber: 1,
      pageSize: ResultsNumConstants.PageSize,
    };
    setP(paging);
    const sortOption = sortByASBV;

    getSearchAnimals(paging, sortOption, source);

    return () => {
      source.cancel();
    };
  }, [filters, getSearchAnimals, primeFilters, setCurrentFilters, setCurrentPrime, setFiltersStep, sortByASBV]);

  const retry = () => {
    setShowRetry(false);
    try {
      const source = Axios.CancelToken.source();
      getSearchAnimals(p, sortByASBV, source);
    } catch {
      setShowRetry(true);
    }
  };

  return (
    <div className="sg-compare-animals">
      {showChart && (
      <div className="asbv-compare-snapshot">
        <ASBVSnapshot
          animals={chartAnimals}
        />
      </div>
      )}
      <div className="sg-animal-cards">
        {data.results.map((animal, index) => (
          <TableFade
            in
              // customDelay={(index % p.pageSize) * 80}
            key={animal.animalId}
          >
            <AnimalCardSimple
              animalId={animal.animalId}
              key={animal.animalId}
              studId={animal.studId}
              sireVisibility={animal.sireVisibility}
              sireAnimalId={animal.sireAnimalId}
              damVisibility={animal.damVisibility}
              damAnimalId={animal.damAnimalId}
              animalASBVs={animal.asbvs}
              tags={getTags(animal).filter((t) => showTag(t, animal, 'all'))}
              db={db}
              trait={subIndex}
            />
          </TableFade>
        ))}
        {showRetry && (
          <SearchError
            size={data.results.length ? 'small' : 'large'}
            retry={retry}
          />
        )}
      </div>
    </div>
  );
};
